import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { LanguageDto, DropdownLanguagesStore } from '../../stores/configuration/locations/languages-store';
import { nameof } from '../../utils/object';
import { ItemReference, QueryResult } from '../../stores/dataStore';
import { isNullOrWhiteSpaces, customEqualCompare } from '../../utils/useful-functions';
import { SelectionInput } from 'widgets/form/selectionInput';

interface LanguageEditorProps extends WithTranslation {
  value: ItemReference | string;
  onChange: (value: ItemReference) => void;
  className?: string;
  nullable?: boolean;
  languages?: DropdownLanguagesStore;
  reloadOnChange?: boolean;
  clearable?: boolean;
  placeholder?: string;
  onlyForTests?: boolean;
  readOnly?: boolean;
}

interface LanguageEditorState {
  value: ItemReference | string;
  query?: (searchQuery: string) => Promise<QueryResult<ItemReference>>;
}

@connect(['languages', DropdownLanguagesStore])
class LanguageEditor extends React.Component<LanguageEditorProps, LanguageEditorState> {
  private get languagesStore() {
    return this.props.languages;
  }

  constructor(props: LanguageEditorProps) {
    super(props);
    this.state = {
      value: this.props.value,
      query: this.getLanguageMethod()
    };
  }

  componentDidUpdate(prevProps) {
    const { value, reloadOnChange, onlyForTests } = this.props;
    if (!customEqualCompare(prevProps.value, value) && reloadOnChange) {
      this.setState({
        value,
        query: this.getLanguageMethod()
      });
    }
    if (!customEqualCompare(prevProps.onlyForTests, onlyForTests)) {
      const prev = this.state.value;
      this.setState(
        {
          value: undefined,
          query: this.getLanguageMethod()
        },
        () => !customEqualCompare(prev, undefined) && this.props.onChange && this.props.onChange(undefined)
      );
    }
  }

  @autobind
  private getLanguageMethod() {
    const method = async (search: string) => {
      const f = [];
      if (this.props.onlyForTests != null) {
        f.push({ hasTnaAvailable: !!this.props.onlyForTests });
      }
      const result = await this.languagesStore.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<LanguageDto>('language'), useProfile: false }],
        filter: isNullOrWhiteSpaces(search) ? f : [...f, `contains(tolower(${nameof<LanguageDto>('language')}), '${search.toLowerCase()}')`]
      });
      return Object.assign({}, result, { items: result.items.map(c => ({ id: c.id, title: c.language })) }) as QueryResult<ItemReference>;
    };
    return method;
  }

  public render() {
    const val = this.state.value;
    const query = this.state.query;
    return (
      <SelectionInput
        readOnly={this.props.readOnly}
        content={item => <div>{item.title}</div>}
        searchable
        className={this.props.className}
        nullable={this.props.nullable}
        query={query}
        value={val}
        onChange={value => this.props.onChange(value as any)}
        clearable={this.props.clearable}
        placeholder={this.props.placeholder}
      />
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(LanguageEditor);
